import { memo } from 'react';
import { ScrollTo } from '../../ScrollTo/ScrollTo/ScrollTo';
import AccordionSummary from '../AccordionSummary/AccordionSummary';
import { AccordionDetails } from '../AccordionDetails/AccordionDetails';
import { BaseAccordionSection } from '../BaseAccordionSection/BaseAccordionSection';
import { AccordionSectionProps } from './AccordionSection.types';
import { GMErrorMessage, GMFormWrapper } from '../../Form';
import css from './AccordionSection.module.scss';

/**
 * This control is a single expandable section that can exist as an
 * accordion on its own. However when grouped together, it can create a series of expandable
 * sections that more commonly used as an accordion.
 *
 * @note Must be rendered inside of a `FormProvider`.
 *
 * @param id - {@link AccordionSectionProps.id id} description.
 * @param ariaControls - {@link AccordionSectionProps.ariaControls ariaControls} description.
 * @param headingLabel - {@link AccordionSectionProps.headingLabel headingLabel} description.
 * @param error - {@link AccordionSectionProps.error error} description.
 * @param errorLabel - {@link AccordionSectionProps.errorLabel errorLabel} description.
 * @param onClick - {@link AccordionSectionProps.onClick onClick} description.
 * @param scroll - {@link AccordionSectionProps.scroll scroll} description.
 * @param expanded - {@link AccordionSectionProps.expanded expanded} description.
 * @param disabled - {@link AccordionSectionProps.disabled disabled} description.
 * @param children - {@link AccordionSectionProps.children children} description.
 */
export const AccordionSection = memo(({
    id,
    ariaControls,
    headingLabel,
    error,
    errorLabel,
    onClick,
    scroll,
    expanded,
    disabled,
    children,
}: AccordionSectionProps) => {
    return (
        <ScrollTo scroll={scroll}>
            <GMFormWrapper>
                <BaseAccordionSection
                    classes={{
                        root: css.accordionSection,
                        expanded: css.expanded,
                    }}
                    elevation={0}
                    expanded={expanded}
                    disabled={disabled}
                >
                    <AccordionSummary
                        id={id}
                        ariaControls={ariaControls}
                        headingLabel={headingLabel}
                        error={error}
                        expanded={expanded}
                        onClick={onClick}
                    />
                    <GMErrorMessage
                        wrapperClassName={css.errorWrapper}
                        error={error}
                    >
                        {errorLabel}
                    </GMErrorMessage>
                    <AccordionDetails
                        id={ariaControls}
                        ariaLabeledBy={id}
                    >
                        {children}
                    </AccordionDetails>
                </BaseAccordionSection>
            </GMFormWrapper>
        </ScrollTo>
    );
});
